import { useState, useEffect } from 'react'
import s from './ProposalHtmlTemplates.scss'
import { FaPlusCircle } from 'react-icons/fa'
import { AddBlockModal } from 'components/common/modals/AddBlockModal'
import { CreateProposalModal } from './CreateProposalModal'
import { InstallProposalModal } from './InstallProposalModal'
import { useImmer } from 'use-immer'
import { BlockList } from './BlockList'
import { TemplateList } from './TemplateList'
import update from 'immutability-helper'
import { toCategories, fromTemplate, toBlock, fromBlock } from './serializers'
import { toTemplates } from 'utils/serializers'
import { makeGetRequest, makePostRequest, makeDeleteRequest, makePutRequest } from 'utils/api'
import { useLoading } from 'hooks'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { sortAlphabetically } from 'utils/helpers'
import { AddButton } from 'components/common/AddButton'
import { withSweetAlert } from 'hocs'

const initialPages = [
  {
    content: []
  }
]

const initialBlock = {
  name: '',
  category: '',
  visible: 'everyone',
  type: 'block',
  content: ''
}

const ProposalHtmlTemplates = () => {
  const [isAddBlockModalVisible, setIsAddBlockModalVisible] = useState(false)
  const [isCreateHtmlProposalModalVisible, setIsCreateHtmlProposalModalVisible] = useState(false)
  const [isInstallHtmlProposalModalVisible, setIsInstallHtmlProposalModalVisible] = useState(false)
  const [pages, setPages] = useImmer(initialPages)
  const [categories, setCategories] = useState([])
  const [categoryList, setCategoryList] = useState([])
  const [templates, setTemplates] = useState([])
  const [predesignedTemplates, setPredesignedTemplates] = useState([])
  const [block, setBlock] = useImmer(initialBlock)
  const [template, setTemplate] = useImmer({})
  const [, withLoadingLocks] = useLoading()
  const reduxDispatch = useDispatch()

  useEffect(() => {
    ;(async () => {
      const result = await withLoadingLocks(makeGetRequest('/v1/proposaltemplates/html/sections'))
      const [sections, list] = toCategories(result)
      setCategories(sections)
      setCategoryList(list)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      const result = await withLoadingLocks(makeGetRequest('/v1/proposaltemplates/html'))
      setTemplates(sortAlphabetically(toTemplates(result), 'name'))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      const result = await withLoadingLocks(makeGetRequest('/v1/predesignedproposaltemplates/html'))
      setPredesignedTemplates(result)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveTemplate = async (json, html, details) => {
    if (!JSON.parse(json)[0].content.length) return
    try {
      if (template?.id) {
        const response = await withLoadingLocks(
          makePutRequest(`/v1/proposaltemplates/html/${template.id}`, fromTemplate(json, details))
        )
        const index = templates.findIndex(t => t.id === template.id)
        const newState = update(templates, {
          [index]: { $set: toTemplates([response])[0] }
        })
        setTemplates(newState)
      } else {
        const response = await withLoadingLocks(
          makePostRequest('/v1/proposaltemplates/html', fromTemplate(json, details))
        )
        setTemplates([...templates, toTemplates([response])[0]])
      }
      setIsCreateHtmlProposalModalVisible(false)
      resetPages()
      setTemplate({})
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Template created successfully',
          level: 'success'
        }
      })
    } catch (e) {
      setIsCreateHtmlProposalModalVisible(false)
      resetPages()
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem saving template',
          level: 'error'
        }
      })
    }
  }

  const saveBlock = async (content, destroyEditor) => {
    try {
      if (block.id) {
        const response = await withLoadingLocks(
          makePutRequest(`/v1/proposaltemplates/html/block/${block.id}`, fromBlock(block, content))
        )

        const index = categories[block.category.label].findIndex(c => c.id === block.id)
        const state = update(categories, {
          [block.category.label]: {
            [index]: { $set: toBlock(response, block.category.value, block.category.label) }
          }
        })
        setCategories(state)
      } else {
        const response = await withLoadingLocks(
          makePostRequest('/v1/proposaltemplates/html/block', fromBlock(block, content))
        )

        const state = update(categories, {
          [block.category.label]: {
            $push: [toBlock(response, block.category.value, block.category.label)]
          }
        })
        setCategories(state)
      }
      destroyEditor && destroyEditor()
      resetBlock()
      setIsAddBlockModalVisible(false)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Block saved successfully',
          level: 'success'
        }
      })
    } catch (e) {
      destroyEditor && destroyEditor()
      resetBlock()
      setIsAddBlockModalVisible(false)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem saving block',
          level: 'error'
        }
      })
    }
  }

  const deleteBlock = async (id, category) => {
    try {
      await withLoadingLocks(makeDeleteRequest(`/v1/proposaltemplates/html/block/${id}`))
      const index = categories[category].findIndex(c => c.id === id)
      const state = update(categories, {
        [category]: { $splice: [[index, 1]] }
      })
      setCategories(state)

      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Block deleted successfully',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem deleting block',
          level: 'error'
        }
      })
    }
  }

  const cloneBlock = async (id, category) => {
    try {
      const block = await withLoadingLocks(
        makePostRequest(`/v1/proposaltemplates/html/block/duplicate/${id}`)
      )
      const categoryObj = categoryList.find(c => c.label === category)
      const state = update(categories, {
        [category]: { $push: [toBlock(block, categoryObj.value, categoryObj.label)] }
      })
      setCategories(state)

      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Block cloned successfully',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem cloning block',
          level: 'error'
        }
      })
    }
  }

  const cloneTemplate = async id => {
    try {
      const template = await withLoadingLocks(makePostRequest(`/v1/proposaltemplates/clone/${id}`))
      setTemplates([...templates, toTemplates([template])[0]])

      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Template cloned successfully',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem cloning template',
          level: 'error'
        }
      })
    }
  }

  const deleteTemplate = id => {
    const sweetAlertFactory = withSweetAlert(
      <div>
        <p style={{ color: 'red' }}>This action can&#39;t be reverted</p>
      </div>
    )
    sweetAlertFactory(
      {
        title: `Are you sure you want to delete this template?`,
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      async () => {
        try {
          await withLoadingLocks(makeDeleteRequest(`/v1/proposaltemplates/${id}`))
          const index = templates.findIndex(t => t.id === id)
          const arr = [...templates]
          arr.splice(index, 1)
          setTemplates(arr)

          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'Template deleted successfully',
              level: 'success'
            }
          })
        } catch (e) {
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'There was a problem deleting template',
              level: 'error'
            }
          })
        }
      },
      { cancelConfirmation: false }
    )
  }

  const openEditBlockModal = block => {
    setBlock(() => block)
    setIsAddBlockModalVisible(true)
  }

  const openEditTemplateModal = async template => {
    try {
      const { content } = await withLoadingLocks(
        makeGetRequest(`/v1/proposaltemplates/${template.id}`)
      )
      setTemplate(() => ({ ...template, content }))
      setPages(() => JSON.parse(content))
      setIsCreateHtmlProposalModalVisible(true)
    } catch (e) {
      setIsCreateHtmlProposalModalVisible(true)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem fetching template',
          level: 'error'
        }
      })
    }
  }

  const resetBlock = () => {
    setBlock(() => initialBlock)
  }

  const resetPages = () => {
    setPages(() => initialPages)
  }

  return (
    <>
      <AddButton onClick={() => setIsCreateHtmlProposalModalVisible(true)} className={s.greenBtn}>
        Add New Proposal Template
      </AddButton>
      <AddButton onClick={() => setIsInstallHtmlProposalModalVisible(true)} className={s.greenBtn}>
        Install Proposal Template
      </AddButton>
      {templates.length > 0 && (
        <TemplateList
          templates={templates}
          deleteTemplate={deleteTemplate}
          openEditModal={openEditTemplateModal}
          cloneTemplate={cloneTemplate}
        />
      )}
      <div className={s.blocksWrapper}>
        <span className={s.title}>Proposal Blocks</span>
        <AddButton className={s.greenBtn} onClick={() => setIsAddBlockModalVisible(true)}>
          <FaPlusCircle className={s.addBlockIcon} /> Add Proposal Block
        </AddButton>
      </div>

      {categories && Object.keys(categories).length > 0 && (
        <>
          {Object.keys(categories)
            .sort()
            .map(category => {
              if (!categories[category].length) return null
              return (
                <BlockList
                  key={category}
                  category={category}
                  blocks={categories[category]}
                  deleteBlock={id => deleteBlock(id, category)}
                  openEditModal={openEditBlockModal}
                  cloneBlock={id => cloneBlock(id, category)}
                />
              )
            })}
        </>
      )}

      {isAddBlockModalVisible && (
        <AddBlockModal
          isAddBlockModalVisible={isAddBlockModalVisible}
          setIsAddBlockModalVisible={setIsAddBlockModalVisible}
          saveBlock={saveBlock}
          setBlock={setBlock}
          block={block}
          resetBlock={resetBlock}
          categoryList={categoryList}
        />
      )}
      {isCreateHtmlProposalModalVisible && (
        <CreateProposalModal
          template={template}
          templates={templates}
          setTemplate={setTemplate}
          isCreateHtmlProposalModalVisible={isCreateHtmlProposalModalVisible}
          setIsCreateHtmlProposalModalVisible={setIsCreateHtmlProposalModalVisible}
          pages={pages}
          resetPages={resetPages}
          categories={categories}
          saveTemplate={saveTemplate}
          setPages={setPages}
        />
      )}
      {isInstallHtmlProposalModalVisible && (
        <InstallProposalModal
          predesignedTemplates={predesignedTemplates}
          isInstallHtmlProposalModalVisible={isInstallHtmlProposalModalVisible}
          setIsInstallHtmlProposalModalVisible={setIsInstallHtmlProposalModalVisible}
          setTemplates={setTemplates}
          templates={templates}
        />
      )}
    </>
  )
}

export default ProposalHtmlTemplates
